
/**
 * Opening the Unity 3D editor from the rewrite (ADR-051).
 *
 * The inverse of everything else in this folder: the panel is what Unity embeds in US, and this is how an author
 * gets to Unity in the first place. Without it the whole WebGL path is unreachable from our UI — the panel is
 * only ever opened *by* Unity, so with no launch an author would have to know the build's URL.
 */

/** What the editor should open on. `manualId` null ⇒ the facility's own 3D setup (ADR-051 §4). */
export interface UnityLaunchTarget {
  facilityId: number;
  /** The manual to open, or null for a facility-scoped session — which the legacy encodes as `0`. */
  manualId: number | null;
  /** The Keycloak realm, from the verified session — never parsed from a host. */
  realm: string;
  /** The author's current language; Unity upper-cases it and accepts `DE` / `EN`. */
  locale: string;
  /** `edit` is the rail button; `view` exists because the legacy's `StartWebView` takes it. */
  mode: "edit" | "view";
}

/**
 * The `webView` context Unity parses (ADR-051 D2).
 *
 * Positional and semicolon-separated: `facilityId;manualId;realm;LANG;{view|edit};{staging|prod}`. Unity reads it
 * with `URLParameters.ReadParemeters()` (sic), and the shape is load-bearing in a way that is easy to break:
 *
 * **It must NOT be percent-encoded.** Unity's own `XrcStripAuthSecrets` avoids `URL`/`URLSearchParams` for exactly
 * this reason — *"deren toString() re-encodiert den webView-Wert (`;` → `%3B`), was ReadParemeters() beim
 * naechsten Load zerlegt"*. Building it with `URLSearchParams` here would produce a string that looks right in the
 * address bar and parses to nothing on the other side.
 *
 * `manualId` is **`0`** when absent, not omitted — `ApplicationManager.GotFacility` branches on `manualId != 0` to
 * decide between loading a manual and running the facility's scene setup, so the slot must be filled.
 */
export function unityWebViewContext(target: UnityLaunchTarget, editorBaseUrl: string, stageUrl: string = editorBaseUrl): string {
  // The stage flag is derived from the BUILD URL, exactly as Unity derives its own:
  // `Application.absoluteURL.Contains("xrcreatorWebURPStaging")` (`ApplicationManager.Start`). One value carries
  // both facts, and the two sides cannot disagree about which environment this is.
  //
  // The legacy instead tests its realm's DISPLAY NAME for "staging" (`IsStagingRealm`) — the same shape as the
  // `username.Contains("superadmin")` gate ADR-044 §7.4 flagged, and it would call a realm named "xr-area
  // staging-ground" staging forever.
  // `stageUrl`: a build outside the two real paths (the 3D test) takes the environment from the real editor URL instead.
  const stage = stageUrl.includes("xrcreatorWebURPStaging") ? "staging" : "prod";
  return [
    String(target.facilityId),
    String(target.manualId ?? 0),
    target.realm,
    target.locale.toUpperCase(),
    target.mode,
    stage,
  ].join(";");
}

/**
 * The full URL to open, or **null where no editor is deployed** (local).
 *
 * Null is a real answer, not a failure: the caller disables its control and says why, rather than navigating to a
 * build that does not exist. Concatenated rather than composed through `URL`, for the encoding reason above.
 *
 * **No authorization request is built here** (ADR-051 D1). The legacy pre-authorizes so Unity boots once, but it
 * can only do that because both sides share a hardcoded PKCE verifier (SECURITY-FINDINGS A19) — copying it would
 * put a known-broken credential in this bundle permanently. Unity self-authenticates when it finds no code, at
 * the cost of booting twice; that trade is D1, and D1a is the small Unity-side change that removes it.
 */
export function unityEditorLaunchUrl(target: UnityLaunchTarget, editorBaseUrl: string | null | undefined, stageUrl?: string | null): string | null {
  if (!editorBaseUrl) return null;
  return `${editorBaseUrl}?webView=${unityWebViewContext(target, editorBaseUrl, stageUrl ?? editorBaseUrl)}`;
}

/** Why the launch is unavailable, so the control can say which — they are not the same problem to the author. */
export type UnityLaunchBlocked =
  /** No Unity build is deployed for this environment (local). Nothing the author can do. */
  | "no-editor"
  /** The manual or its facility has not resolved yet. Transient. */
  | "no-context"
  /** The author may not edit this manual (ADR-051 D6). */
  | "read-only";

export type UnityLaunch = { url: string; blocked: null } | { url: null; blocked: UnityLaunchBlocked };

/**
 * The whole launch decision, as one pure function (ADR-051 D5/D6).
 *
 * Extracted from the component deliberately. Testing this through a mounted `ManualEditor` meant standing up the
 * editor's locks, permissions and a dozen mutation hooks to assert one URL — the scaffolding fails for reasons
 * that have nothing to do with the launch, and a green result would not have meant much either.
 *
 * **Order matters.** `read-only` is reported only when a launch would OTHERWISE be possible, so a local developer
 * on a read-only manual is told the honest thing — there is no editor here — rather than being sent to fix a
 * permission that would not have helped.
 */
export function resolveUnityLaunch(opts: {
  realm: string | undefined;
  facilityId: number | null;
  manualId: number;
  locale: string;
  editorUrl: string | null | undefined;
  readOnly: boolean;
  /** `edit` by default; the reader opens a manual without a 2D view in `view` (ADR-057). */
  mode?: "edit" | "view";
}): UnityLaunch {
  if (!opts.editorUrl) return { url: null, blocked: "no-editor" };
  if (!opts.realm || opts.facilityId === null) return { url: null, blocked: "no-context" };
  if (opts.readOnly) return { url: null, blocked: "read-only" };

  const url = unityEditorLaunchUrl(
    { facilityId: opts.facilityId, manualId: opts.manualId, realm: opts.realm, locale: opts.locale, mode: opts.mode ?? "edit" },
    opts.editorUrl,
  );
  return url === null ? { url: null, blocked: "no-editor" } : { url, blocked: null };
}

/**
 * The Unity build for the editor's "3D test" (owner 2026-11-03): the editor as a layer behind the steps. A test, so it exists
 * locally and on staging only: production never gets it. Locally the build is served SAME-ORIGIN by the proxy at `/unity/`
 * (ADR-059 D4 — the Web Editor Unity reads the local new API relative to its page; `VITE_UNITY_TEST_URL` overrides);
 * staging loads the test build the owner uploaded next to the real ones. Staging is recognised by its editor URL, which the
 * edge stamps per environment.
 */
export const UNITY_TEST_BUILD_URL = "https://xrarea.arcreator.at/XRCreator_Web_Build_Test/index.html";
export function unityTestBuildUrl(dev: boolean, editorUrl: string | null | undefined, localOverride: string | undefined): string | null {
  if (dev) return localOverride ?? `${globalThis.location?.origin ?? ""}/unity/index.html`;
  return editorUrl?.includes("xrcreatorWebURPStaging") ? UNITY_TEST_BUILD_URL : null;
}

/**
 * Unity's Keycloak for the 3D test on staging (ADR-058 D9). There our token comes from `my-dev`, while Unity's staging realm
 * and the legacy API take only `my-prod`, so the test signs in a second time, at Unity's Keycloak, with the client Unity itself
 * uses (`RealmData/xr-area staging.asset`, a public client). A realm without an entry keeps our own token (D8).
 */
const UNITY_TEST_KEYCLOAK: Readonly<Record<string, { baseUrl: string; realm: string; clientId: string }>> = {
  "xr-area": { baseUrl: "https://my-prod.create.at/auth", realm: "xr-area", clientId: "arcreator" },
};
/**
 * Which environment Unity runs the 3D test in (the sixth `webView` field). With its own sign-in at Unity's Keycloak (D9) the test
 * reads production (`svc.ar-creator`, realm "XR-Area"): our staging database is a copy of production, the legacy staging one is
 * not, so a manual opened here mostly exists only there (owner 2026-11-03). Otherwise the environment follows the editor URL.
 * Returned as a "stage URL" for `unityEditorLaunchUrl`, which reads only whether it names the staging path.
 */
export function unityTestStageUrl(editorUrl: string | null | undefined, ownSignIn: boolean): string | null | undefined {
  return ownSignIn ? UNITY_TEST_BUILD_URL : editorUrl;
}

export function unityTestKeycloak(dev: boolean, testBuildUrl: string | null, realm: string | undefined): { baseUrl: string; realm: string; clientId: string } | null {
  if (dev || testBuildUrl !== UNITY_TEST_BUILD_URL || !realm) return null;
  return Object.hasOwn(UNITY_TEST_KEYCLOAK, realm) ? UNITY_TEST_KEYCLOAK[realm]! : null;
}
